import gql from 'graphql-tag';

const FRAGMENTS = gql`
  fragment ChatMessage on chat_messages {
    id
    created_at
    message
    attachment
    room_id
    user {
      user_first_name
      user_last_name
      user_preferred_name
      user_pic
      user_id
      user_type
    }
  }
  fragment ChatRooom on chat_rooms {
    id
    room_id: id
    room_name
    last_message_timestamp
    last_message
  }
  fragment RoomUser on chat_room_users {
    id
    user_id
    room_id
    last_activity
  }
`;

export const GET_ROOMS_BY_STUDENT = gql`
  query roomsByStudent($userId: Int!, $roomName: String) {
    rooms: chat_room_users(
      where: {
        user_id: { _eq: $userId }
        room: { room_name: { _eq: $roomName } }
      }
    ) {
      ...RoomUser
      room {
        ...ChatRooom
      }
    }
  }
  ${FRAGMENTS}
`;

export const GET_ALL_STUDENT_ROOMS = gql`
  query roomsByContextUserId($userId: Int!) {
    chat_rooms(
      where: { context: { _eq: "student" }, context_id: { _eq: $userId } }
      order_by: [{ created_at: desc }]
    ) {
      ...ChatRooom
    }
  }
  ${FRAGMENTS}
`;

export const GET_ROOMS_BY_STUDENT_COACHES = gql`
  query roomsByStudent($userId: Int!) {
    rooms: chat_room_users(
      where: {
        user_id: { _eq: $userId }
        room: { room_name: { _eq: "coaches" } }
      }
    ) {
      ...RoomUser
      room {
        ...ChatRooom
      }
    }
  }
  ${FRAGMENTS}
`;

export const LISTEN_MESSAGES = gql`
  subscription messages($roomId: Int!) {
    messages: chat_messages(
      where: { room_id: { _eq: $roomId } }
      order_by: [{ created_at: asc }]
    ) {
      ...ChatMessage
    }
  }
  ${FRAGMENTS}
`;

export const LISTEN_ROOM_MESSAGES = gql`
  subscription messages($id: Int!) {
    messages: chat_messages(
      where: { chat_room: { id: { _eq: $id } } }
      order_by: [{ created_at: asc }]
    ) {
      ...ChatMessage
    }
  }
  ${FRAGMENTS}
`;

export const INSERT_MESSAGE = gql`
  mutation addChat($data: chat_messages_insert_input!) {
    message: insert_chat_messages_one(object: $data) {
      ...ChatMessage
    }
  }
  ${FRAGMENTS}
`;

export const GET_COACH_STUDENTS = gql`
  subscription getRooms {
    rooms: v_chat_rooms(where: { room_name: { _eq: "coaches" } }) {
      id
      room_name
      message
      room_users(where: { user: { user_type: { _eq: "student" } } }) {
        user {
          user_first_name
          user_last_name
          user_preferred_name
          user_pic
          user_type
        }
      }
    }
  }
`;

export const GET_STUDENTS = gql`
  subscription getAdminRooms {
    rooms: v_chat_rooms(where: { room_name: { _eq: "admin" } }) {
      id
      room_name
      message
      room_users(where: { user: { user_type: { _eq: "student" } } }) {
        user {
          user_first_name
          user_last_name
          user_preferred_name
          user_pic
          user_type
        }
      }
    }
  }
`;

export const GET_USER = gql`
  query getUser($id: Int!) {
    user: users_by_pk(user_id: $id) {
      user_first_name
      user_id
      user_last_name
      user_preferred_name
      user_pic
    }
  }
`;

export const GET_ROOM = gql`
  query room($id: Int!) {
    room: chat_rooms_by_pk(id: $id) {
      id
      room_name
      group {
        id
        name
      }
      student {
        id: user_id
        user_first_name
        user_last_name
        user_preferred_name
        user_pic
      }
      users {
        user {
          user_first_name
          user_last_name
          user_preferred_name
          user_pic
          user_type
          user_id
        }
      }
    }
  }
`;

export const UPDATE_USER_ROOM_STATUS = gql`
  mutation updateUserRoom(
    $roomId: Int!
    $userId: Int!
    $timestamp: timestamptz!
  ) {
    update_chat_room_users(
      where: { room_id: { _eq: $roomId }, user_id: { _eq: $userId } }
      _set: { last_activity: $timestamp }
    ) {
      returning {
        ...RoomUser
      }
    }
  }
  ${FRAGMENTS}
`;

export const LISTEN_USER_UNREAD_COUNT = gql`
  subscription listenUnreadCount($userId: Int!) {
    v_chat_unread_counts(where: { user_id: { _eq: $userId } }) {
      count
      room_id
      room {
        room_name
      }
    }
  }
`;

export const LISTEN_USER_ROOMS = gql`
  subscription rooms(
    $userId: Int!
    $query: String
    $sortParams: v_chat_rooms_order_by!
  ) {
    rooms: v_chat_rooms(
      where: {
        user_id: { _eq: $userId }
        student: {
          _or: [{ deleted: { _neq: true } }, { deleted: { _is_null: true } }]
        }
        _or: [
          { room: { student: { user_first_name: { _ilike: $query } } } }
          { room: { student: { user_last_name: { _ilike: $query } } } }
        ]
      }
      order_by: [$sortParams, { last_message_at: desc_nulls_last }]
    ) {
      id
      context
      context_id
      room_name
      message
      last_message_at
      last_activity
      unread
      room {
        id
        student {
          id: user_id
          user_first_name
          user_last_name
          user_preferred_name
          user_pic
        }
      }
    }
  }
`;

export const LISTEN_GROUP_ROOMS = gql`
  subscription rooms($userId: Int!, $query: String) {
    rooms: v_chat_rooms(
      where: {
        user_id: { _eq: $userId }
        room_name: { _eq: "groups" }
        room: { group: { name: { _ilike: $query } } }
      }
      order_by: [{ unread: desc }, { last_message_at: desc_nulls_last }]
    ) {
      id
      room_name
      message
      last_message_at
      last_activity
      unread
      room {
        id
        group {
          id
          name
        }
        student {
          id: user_id
          user_first_name
          user_last_name
          user_preferred_name
        }
      }
    }
  }
`;

export const GET_GROUP_ROOMS = gql`
  subscription getRooms($userId: Int!, $query: String) {
    rooms: chat_rooms(
      where: {
        room_name: { _eq: "groups" }
        group: { name: { _ilike: $query } }
      }
    ) {
      id
      room_name
      last_message
      last_message_timestamp
      group {
        id
        name
      }
      messages {
        created_at
      }
      users(where: { user_id: { _eq: $userId } }) {
        last_activity
      }
    }
  }
`;

export const GET_ROOMS = gql`
  query getRooms($query: String, $sortallChannelsParams: chat_rooms_order_by!) {
    rooms: chat_rooms(
      where: {
        student: {
          _or: [{ deleted: { _neq: true } }, { deleted: { _is_null: true } }]
        }
        _or: [
          { student: { user_first_name: { _ilike: $query } } }
          { student: { user_last_name: { _ilike: $query } } }
        ]
      }
      order_by: [$sortallChannelsParams]
    ) {
      id
      room_name
      last_message
      last_message_at: last_message_timestamp
      student {
        id: user_id
        user_first_name
        user_last_name
        user_preferred_name
      }
    }
  }
`;

export const CREATE_GROUP_ROOM = gql`
  mutation createRoom($lcId: Int!, $hcId: Int!, $userId: Int!) {
    insert_chat_rooms(
      objects: {
        context: "group"
        room_name: "groups"
        users: {
          data: [{ user_id: $lcId }, { user_id: $hcId }, { user_id: $userId }]
        }
      }
    ) {
      returning {
        id
      }
    }
  }
`;

export const UPSERT_CHATROOM_USERS = gql`
  mutation upsertChatUsers($data: [chat_room_users_insert_input!]!) {
    insert_chat_room_users(
      objects: $data
      on_conflict: {
        constraint: chat_room_users_room_id_user_id_key
        update_columns: [user_id]
      }
    ) {
      affected_rows
    }
  }
`;
